// src/pages/BorrowerProfile.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Home,
  Calendar,
  Save,
  X,
  Pencil,
} from "lucide-react";
import Hero from '../../components/ui/Hero';
import { getBorrowerProfile, updateBorrowerProfile, createBorrowerProfile } from '../../api/borrowerPortal/borrowerProfile';
import { STORAGE_KEYS } from '../../utils/constants';
import { getBorrowerEmail, isAuthenticated } from '../../utils/auth';
import PhoneField from "../../components/ui/PhoneField.jsx";
import { isValidPhone } from "../../utils/validatePhone.js";

const LS_KEY = "borrower_profile";

/**
 * Frontend -> Backend (MATCHES YOUR API FIELD NAMES)
 * NI REMOVED intentionally.
 */
function toBackendFields(front, profileName) {
  // Ensure user field matches the logged-in user (profileName)
  // The user field must match the document name for permission checks
  const userEmail = profileName || front.user || "";
  
  return {
    first_name: front.firstName || "",
    last_name: front.lastName || "",
    email_address: front.email || "",
    date_of_birth: front.dob || "",
    phone_number: front.phone || "",
    address_line_1: front.address1 || "",
    address_line_2: front.address2 || "",
    city: front.city || "",
    country: front.country || "United Kingdom",
    postcode: front.postcode || "",
    user: userEmail, // Must match the logged-in user for permissions
  };
}

/**
 * Backend -> Frontend (MATCHES YOUR API FIELD NAMES)
 * NI IGNORED intentionally.
 */
function fromBackendFields(doc) {
  const d = doc || {};
  return {
    firstName: d.first_name ?? "",
    lastName: d.last_name ?? "",
    email: d.email_address ?? "",
    phone: d.phone_number ?? "",
    address1: d.address_line_1 ?? "",
    address2: d.address_line_2 ?? "",
    city: d.city ?? "",
    postcode: d.postcode ?? "",
    country: d.country ?? "United Kingdom",
    dob: d.date_of_birth ?? "",
  };
}

export default function BorrowerProfile() {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address1: "",
    address2: "",
    city: "",
    postcode: "",
    country: "United Kingdom",
    dob: "",
  });

  const [draft, setDraft] = useState(form);
  const [editing, setEditing] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // Uses doc name like: "test22@example.com"
  // Get email from storage using the same keys as sign-in/sign-up
  // Priority: email from sign-in storage > session user > other storage
  const profileName = useMemo(() => {
    // First, try to get the email that was used during sign-in (most reliable)
    // This is stored in STORAGE_KEYS.BORROWER_EMAIL during sign-in
    const signInEmail = 
      sessionStorage.getItem(STORAGE_KEYS.BORROWER_EMAIL) ||
      localStorage.getItem(STORAGE_KEYS.BORROWER_EMAIL) ||
      sessionStorage.getItem("BORROWER_EMAIL") ||
      localStorage.getItem("BORROWER_EMAIL");
    
    if (signInEmail) {
      console.log("✅ Found sign-in email from storage:", signInEmail);
      return signInEmail;
    }
    
    // Second, try to get the logged-in user from session (from login response)
    const sessionUser = sessionStorage.getItem("user") || localStorage.getItem("user");
    if (sessionUser) {
      console.log("✅ Found logged-in user from session:", sessionUser);
      return sessionUser;
    }
    
    // Third, try the utility function (checks both localStorage and sessionStorage)
    const emailFromAuth = getBorrowerEmail();
    if (emailFromAuth) {
      console.log("✅ Found borrower email from auth utility:", emailFromAuth);
      return emailFromAuth;
    }
    
    // Fallback to direct storage access (for backward compatibility)
    const fallbackEmail = (
      localStorage.getItem("borrower_email") ||
      localStorage.getItem("user_email") ||
      localStorage.getItem("email") ||
      ""
    );
    
    if (!fallbackEmail) {
      console.warn("⚠️ No borrower email found in storage. User may not be signed in.");
    }
    
    return fallbackEmail;
  }, []);

  const startEdit = () => {
    setDraft(form);
    setEditing(true);
    setErrorMsg("");
  };

  const cancelEdit = () => {
    setDraft(form);
    setEditing(false);
    setErrorMsg("");
  };

  const update = (e) =>
    setDraft((d) => ({
      ...d,
      [e.target.name]: e.target.value,
    }));
  const phoneValid = isValidPhone(draft.phone, { required: false });

  // Load: backend first, fallback to localStorage
  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setErrorMsg("");

      // 1) localStorage fallback (instant)
      try {
        const raw = localStorage.getItem(LS_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);

          // Safety cleanup: remove any old NI remnants
          delete parsed.nationalInsurance;
          delete parsed.national_insurance;
          delete parsed.national_insurance_number;

          if (alive) {
            setForm((prev) => ({ ...prev, ...parsed }));
            setDraft((prev) => ({ ...prev, ...parsed }));
          }
        }
      } catch { }

      // 2) backend (source of truth) - only if profileName exists and user is authenticated
      if (!profileName || profileName.trim() === "") {
        if (alive) {
          setLoading(false);
          if (!isAuthenticated()) {
            setErrorMsg("Please sign in to load your profile.");
          } else {
            setErrorMsg("Unable to retrieve your email. Please sign in again.");
          }
        }
        return;
      }
      
      // Check authentication status
      if (!isAuthenticated()) {
        if (alive) {
          setLoading(false);
          setErrorMsg("Please sign in to load your profile.");
        }
        return;
      }

      try {
        const res = await getBorrowerProfile(profileName);

        // API response structure: { data: { ...doc } }
        // The unwrap function returns { data: doc } or just the doc
        const doc = res?.data ?? res ?? {};
        const mapped = fromBackendFields(doc);

        if (!alive) return;

        setForm(mapped);
        setDraft(mapped);

        // refresh cache
        try {
          localStorage.setItem(LS_KEY, JSON.stringify(mapped));
        } catch { }
      } catch (e) {
        const status = e?.response?.status ?? e?.status;
        
        // Check if it's a network error
        const isNetworkErr = !e.response && 
          (e.message?.includes("Network Error") || 
           e.message?.includes("ERR_NETWORK") ||
           e.code === "ERR_NETWORK" ||
           (typeof navigator !== "undefined" && !navigator.onLine));

        // Only log error if it's not a 404 (expected when profile doesn't exist)
        if (status !== 404 && !isNetworkErr) {
          console.error("Borrower profile load failed:", {
            status,
            profileName,
            message: e?.message,
            data: e?.response?.data,
          });
        }

        if (!alive) return;

        // If 404, profile likely doesn't exist yet — don't show scary msg
        if (status === 404) {
          // Profile doesn't exist yet - this is normal for new users
          // Don't show error, just use localStorage data if available
        } else if (isNetworkErr) {
          // Network error - show offline message
          setErrorMsg("No internet connection. Please check your connection and try again.");
        } else if (status === 403) {
          // Permission denied - user might not be authenticated properly
          setErrorMsg(
            "Unable to load profile. Please ensure you are signed in."
          );
        } else {
          setErrorMsg(
            "Could not load profile from server. Using saved local version (if any)."
          );
        }
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [profileName]);

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrorMsg("");

    // Get the email that was used during sign-in (most reliable for permissions)
    // Priority: sign-in email > session user > profileName
    const currentUser = 
      sessionStorage.getItem(STORAGE_KEYS.BORROWER_EMAIL) ||
      localStorage.getItem(STORAGE_KEYS.BORROWER_EMAIL) ||
      sessionStorage.getItem("BORROWER_EMAIL") ||
      localStorage.getItem("BORROWER_EMAIL") ||
      sessionStorage.getItem("user") ||
      localStorage.getItem("user") ||
      profileName;
    
    // Validate that we have a user email
    if (!currentUser || currentUser.trim() === "") {
      setErrorMsg("Unable to determine logged-in user. Please sign in again.");
      setSaving(false);
      return;
    }
    
    console.log("🔐 Using email for profile save:", currentUser);

    try {
      // Update local cache immediately (nice UX)
      try {
        localStorage.setItem(LS_KEY, JSON.stringify(draft));
      } catch { }

      const payload = toBackendFields(draft, currentUser);
      
      // CRITICAL: Ensure the user field matches the logged-in user
      // The document name in Frappe must match the logged-in user for permissions
      payload.user = currentUser;
      
      console.log("💾 Saving profile with:", {
        profileName: currentUser,
        userField: payload.user,
        emailAddress: payload.email_address
      });

      // Try update first; if not found, create
      // Use currentUser as the document name (must match logged-in user)
      try {
        await updateBorrowerProfile(currentUser, payload);
        console.log("✅ Profile updated successfully");
      } catch (err) {
        const status = err?.response?.status ?? err?.status;
        if (status === 404) {
          // Profile doesn't exist, create it
          console.log("📝 Profile not found, creating new profile...");
          await createBorrowerProfile(payload);
          console.log("✅ Profile created successfully");
        } else {
          throw err;
        }
      }

      setForm(draft);
      setSavedMsg("Profile saved.");
      setTimeout(() => setSavedMsg(""), 1500);
      setEditing(false);
    } catch (e2) {
      console.error("Borrower profile save failed:", {
        profileName,
        message: e2?.message,
        status: e2?.response?.status ?? e2?.status,
        data: e2?.response?.data,
        url: e2?.config?.url,
      });
      
      // Provide more specific error messages
      const status = e2?.response?.status ?? e2?.status;
      if (status === 403) {
        setErrorMsg("Permission denied. Please ensure you are signed in with the correct account.");
      } else if (status === 401) {
        setErrorMsg("Authentication required. Please sign in again.");
      } else if (e2?.message?.includes("CORS")) {
        setErrorMsg("Network error. Please check your connection or contact support.");
      } else {
        setErrorMsg(
          `Save failed: ${e2?.message || "Unknown error"}. Please try again.`
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-dvh bg-white">
      <Hero
        className="-mx-4 sm:-mx-6"
        title="Profile"
        subtitle="Manage your personal and contact information"
        right={
          !editing ? (
            <button
              type="button"
              onClick={startEdit}
              className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-3 py-2 font-semibold hover:bg-white/20"
              disabled={loading}
            >
              <Pencil className="h-4 w-4" /> Edit
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={cancelEdit}
                className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-3 py-2 font-semibold hover:bg-white/20"
              >
                <X className="h-4 w-4" /> Cancel
              </button>
              <button
                type="submit"
                form="profile-form"
                disabled={saving || !phoneValid}
                className={
                  "inline-flex items-center gap-2 rounded-xl bg-white px-3 py-2 font-semibold text-sky-700 " +
                  (saving || !phoneValid ? "opacity-70" : "hover:bg-slate-100")
                }
              >
                <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save"}
              </button>
            </div>
          )
        }
      />

      <form id="profile-form" onSubmit={onSave} className="space-y-6 -mx-4 sm:-mx-6">
        <div className="px-4 sm:px-6">
          {errorMsg ? (
            <div className="text-sm font-semibold text-rose-700 py-2">
              {errorMsg}
            </div>
          ) : null}
          {savedMsg ? (
            <div className="text-sm font-semibold text-emerald-700 py-2">
              {savedMsg}
            </div>
          ) : null}
        </div>

        {/* Personal Information */}
        <section className="bg-white px-4 sm:px-6 py-6 border-t-4 border-sky-400 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2 pb-3 border-b border-sky-100">
            <User className="text-sky-500" size={22} />
            Personal Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
              icon={<User className="w-4 h-4 text-sky-600" />}
              label="First Name"
              name="firstName"
              value={editing ? draft.firstName : form.firstName}
              onChange={update}
              readOnly={!editing}
              loading={loading}
            />
            <Field
              icon={<User className="w-4 h-4 text-sky-600" />}
              label="Last Name"
              name="lastName"
              value={editing ? draft.lastName : form.lastName}
              onChange={update}
              readOnly={!editing}
              loading={loading}
            />
            <Field
              icon={<Mail className="w-4 h-4 text-sky-600" />}
              label="Email Address"
              name="email"
              type="email"
              value={editing ? draft.email : form.email}
              onChange={update}
              readOnly={!editing}
              loading={loading}
            />
            <label className="block">
              <span className="text-sm font-semibold text-slate-700">Phone Number</span>
              {loading ? (
                <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
                  <Phone className="w-4 h-4 text-sky-600" />
                  <div className="h-5 flex-1 bg-slate-100 rounded animate-pulse" />
                </div>
              ) : editing ? (
                <div className="mt-1.5">
                  <PhoneField
                    value={draft.phone}
                    required={false}
                    onChange={(next) => setDraft((d) => ({ ...d, phone: next }))}
                  />
                </div>
              ) : (
                <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
                  <Phone className="w-4 h-4 text-sky-600" />
                  <span className="text-slate-900">{form.phone || "—"}</span>
                </div>
              )}
            </label>
            <Field
              icon={<Calendar className="w-4 h-4 text-sky-600" />}
              label="Date of Birth"
              name="dob"
              type="date"
              value={editing ? draft.dob : form.dob}
              onChange={update}
              readOnly={!editing}
              loading={loading}
            />
          </div>
        </section>

        {/* Address */}
        <section className="bg-white px-4 sm:px-6 py-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2 pb-3 border-b border-sky-100">
            <MapPin className="text-sky-500" size={22} />
            Current Address
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
              icon={<Home className="w-4 h-4 text-sky-600" />}
              label="Address Line 1"
              name="address1"
              value={editing ? draft.address1 : form.address1}
              onChange={update}
              readOnly={!editing}
              loading={loading}
            />
            <Field
              label="Address Line 2"
              name="address2"
              value={editing ? draft.address2 : form.address2}
              onChange={update}
              readOnly={!editing}
              loading={loading}
            />
            <Field
              icon={<MapPin className="w-4 h-4 text-sky-600" />}
              label="City"
              name="city"
              value={editing ? draft.city : form.city}
              onChange={update}
              readOnly={!editing}
              loading={loading}
            />
            <Field
              icon={<MapPin className="w-4 h-4 text-sky-600" />}
              label="Postcode"
              name="postcode"
              value={editing ? draft.postcode : form.postcode}
              onChange={update}
              readOnly={!editing}
              loading={loading}
            />
            <Field
              icon={<MapPin className="w-4 h-4 text-sky-600" />}
              label="Country"
              name="country"
              value={editing ? draft.country : form.country}
              onChange={update}
              readOnly={!editing}
              loading={loading}
            />
          </div>
        </section>

        {/* Footer actions (mobile) */}
        <div className="bg-white px-4 sm:px-6 py-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-semibold text-emerald-700">
            {savedMsg}
          </div>
          {editing && (
            <div className="flex items-center gap-2 md:hidden">
              <button
                type="button"
                onClick={cancelEdit}
                className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-2 font-semibold text-slate-700"
              >
                <X className="h-4 w-4" /> Cancel
              </button>
              <button
                type="submit"
                disabled={saving || !phoneValid}
                className="inline-flex items-center gap-2 rounded-xl bg-sky-500 px-3 py-2 font-semibold text-white hover:bg-sky-600"
              >
                <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save"}
              </button>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}

function Field({
  icon,
  label,
  name,
  value,
  onChange,
  type = "text",
  placeholder,
  readOnly,
  loading = false,
}) {
  const base =
    "w-full bg-transparent outline-none text-slate-900 placeholder:text-slate-400";
  const box =
    "mt-1.5 flex items-center gap-2 rounded-xl border bg-white px-3 py-2";
  return (
    <label className="block">
      <span className="text-sm font-semibold text-slate-700">{label}</span>
      <div
        className={
          box +
          (readOnly
            ? " border-slate-200"
            : " border-sky-200 focus-within:ring-2 focus-within:ring-sky-300 focus-within:border-sky-400")
        }
      >
        {icon ? icon : null}
        {loading ? (
          <div className="h-5 flex-1 bg-slate-100 rounded animate-pulse" />
        ) : (
          <input
            className={base + (readOnly ? " cursor-default" : "")}
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            readOnly={readOnly}
          />
        )}
      </div>
    </label>
  );
}
